import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Import useNavigate
import { TfiZoomIn, TfiZoomOut } from 'react-icons/tfi';
import { RiResetRightLine } from 'react-icons/ri';
import { FiMaximize, FiMinimize } from 'react-icons/fi';
import { GrPowerReset } from "react-icons/gr";
import { IoArrowBack } from 'react-icons/io5'; // Import back arrow icon
// import GlobalLoader from './GlobalLoader';



const DrawingPage = () => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const location = useLocation();
    const navigate = useNavigate(); // Initialize useNavigate
    const searchParams = new URLSearchParams(location.search);
    const imageUrl = searchParams.get('imageUrl');


    const [zoom, setZoom] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isStretched, setIsStretched] = useState(true);
    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const startDragPosition = useRef({ x: 0, y: 0 });

    const handleGoBack = () => {
        window.history.back();
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const zoomFactor = 0.1;

        if (e.deltaY < 0) {
            setZoom((prevZoom) => Math.min(prevZoom + zoomFactor, 8)); // Max zoom level is 8
        } else {
            setZoom((prevZoom) => Math.max(prevZoom - zoomFactor, 1)); // Min zoom level is 1
        }
    };


    const handleZoomIn = () => {
        setZoom((prevZoom) => Math.min(prevZoom + 0.1, 8)); // Max zoom level is 5x
    };

    const handleZoomOut = () => {
        setZoom((prevZoom) => Math.max(prevZoom - 0.1, 1)); // Min zoom level is 1x
    };

    // Handle reset view
    const handleResetView = () => {
        setZoom(1);
        setOffset({ x: 0, y: 0 });
    };

    // Handle mouse drag to move the image around
    const startDragging = (e) => {
        e.preventDefault();
        startDragPosition.current = {
            x: e.clientX - offset.x, // Initial drag position relative to image's position
            y: e.clientY - offset.y,
        };
        setIsDragging(true);
    };

    const stopDragging = () => {
        setIsDragging(false);
    };

    const dragImage = (e) => {
        if (!isDragging) return;

        const dx = e.clientX - startDragPosition.current.x;
        const dy = e.clientY - startDragPosition.current.y;

        // Update the image position based on mouse movement
        setOffset({
            x: dx,
            y: dy,
        });
    };

    // Prevent right-click and certain keyboard actions
    const preventContextMenu = (e) => {
        e.preventDefault(); // Disable right-click
    };

    const preventPrint = (e) => {
        if (e.ctrlKey && e.key === 'p') {
            e.preventDefault(); // Disable Ctrl + P for print
        }
        if (e.metaKey && e.key === 'p') {
            e.preventDefault(); // Disable Cmd + P for print on Mac
        }
    };

    // Add event listener to handle scroll zoom and dragging
    useEffect(() => {
        const container = containerRef.current;
        container.addEventListener('wheel', handleWheel, { passive: false });
        container.addEventListener('mousemove', dragImage);
        container.addEventListener('mousedown', startDragging);
        container.addEventListener('mouseup', stopDragging);
        container.addEventListener('mouseleave', stopDragging); // Stop dragging if mouse leaves
        container.addEventListener('contextmenu', preventContextMenu); // Block right-click

        // Prevent printing (Ctrl + P / Cmd + P)
        window.addEventListener('keydown', preventPrint);

        return () => {
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('mousemove', dragImage);
            container.removeEventListener('mousedown', startDragging);
            container.removeEventListener('mouseup', stopDragging);
            container.removeEventListener('mouseleave', stopDragging);
            container.removeEventListener('contextmenu', preventContextMenu); // Block right-click

            // Clean up event listener for preventing printing
            window.removeEventListener('keydown', preventPrint);
        };
    }, [isDragging, offset]);

    // Toggle stretch functionality
    const toggleStretch = () => {
        setIsStretched(!isStretched);
    };

    const controlButtonStyle = {
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: '20px',
        color: 'var(--primary-text)',
        cursor: 'pointer',
        fontWeight: 'bold',
        padding: '0',
        borderRadius: '0',
        boxShadow: 'none',
        transition: 'color 0.3s ease',
    };

    const containerStyle = {
        width: isStretched ? '100%' : '80vw',
        height: isStretched ? '100%' : '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
        margin: 'auto',
        backgroundColor: 'var(--card-container-bg)',
        transition: 'width 0.3s ease, height 0.3s ease, background-color 0.3s ease',
    };

    const backButtonStyle = {
        position: 'absolute',
        top: '10px',
        left: '20px',
        zIndex: 10,
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: '24px',
        color: 'var(--primary-text)',
        cursor: 'pointer',
        fontWeight: 'bold',
        padding: '0',
        borderRadius: '0',
        marginLeft: "10px",
        boxShadow: 'none',
        transition: 'color 0.3s ease',
    };

    const controlsContainerStyle = {
        position: 'absolute',
        top: '10px',
        right: '20px',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
        alignItems: 'center',
        backgroundColor: 'var(--card-container-bg)',
        padding: '5px 10px',
        borderRadius: '4px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        transition: 'background-color 0.3s ease',
    };

    const backButtonContainer = {
        position: 'absolute',
        top: '10px',
        left: '20px',
        zIndex: 10,
        backgroundColor: 'var(--card-container-bg)',
        padding: '8px 12px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        display: 'flex',
        alignItems: 'center'
    }
    const backButton = {
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: '20px',
        color: 'var(--primary-text)',
        cursor: 'pointer',
        fontWeight: 'bold',
        padding: '0',
        borderRadius: '0',
        display: 'flex',
        alignItems: 'center',
        transition: 'color 0.3s ease'
    }

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            height: '100vh',
            backgroundColor: 'var(--body-bg)',
            transition: 'background-color 0.3s ease'
        }}>
            {/* Back Arrow Button */}

            {/* {!isImageLoaded && <GlobalLoader />} */}
            <div style={backButtonContainer}>
                <button
                    onClick={handleGoBack}
                    style={backButton}
                >
                    <IoArrowBack size={20} />
                </button>
            </div>


            {/* Outer container */}
            <div
                ref={containerRef}
                style={containerStyle}
            >
                {/* Zoom and Stretch controls */}
                <div style={controlsContainerStyle}>
                    <button
                        onClick={handleZoomIn}
                        style={controlButtonStyle}
                    >
                        <TfiZoomIn size={18} />
                    </button>
                    <button
                        onClick={handleZoomOut}
                        style={controlButtonStyle}
                    >
                        <TfiZoomOut size={18} />
                    </button>
                    <button
                        onClick={handleResetView}
                        style={controlButtonStyle}
                    >
                        <GrPowerReset size={18} />
                    </button>
                    <button
                        onClick={toggleStretch}
                        style={controlButtonStyle}
                    >
                        {isStretched ? <FiMinimize size={18} /> : <FiMaximize size={18} />}
                    </button>
                </div>

                {/* Image container */}
                <div
                    style={{
                        transform: `scale(${zoom}) translate(${offset.x}px, ${offset.y}px)`,
                        transformOrigin: 'center',
                        transition: 'transform 0.2s ease-out',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <img
                        ref={imageRef}
                        src={imageUrl}
                        // onLoad={() => setIsImageLoaded(true)}
                        alt="drawing"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DrawingPage;


